import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import {
  normalizeVoucherCategories,
  useVoucherContext,
} from "../../Context/Voucher/VoucherContext";
import { Voucher } from "../../Components/Voucher/Voucher";
import { useGetVouchersList } from "../../APICalling/useApiQueries";
import { useLocation, useNavigate } from "react-router-dom";
import Skeleton from "react-loading-skeleton";
import Loader from "../../Components/Service/Loader";
import { Header } from "../../Components/Header/Header";
import { useSticky } from "../../Utils/Helper";
import { clearSessionStorage } from "../../Utils/SessionStorage";
import styles from "./Style.module.css";
import Placeholder from "../../Components/Placeholder/Placeholder";
import CloseCircleIcon from "../../Components/Icons/CloseCircleIcon";
import VoucherCart from "../../Components/Cart/VoucherCart";
import { VoucherTermsModal } from "../../Components/Popup/VoucherTermsModal/VoucherTermsModal";

const SCROLL_SUPPRESS_MS = 700;
const SECTION_SCROLL_OFFSET = 8;

/** Resolve selected qty for a voucher id (supports slot lines and grouped count). */
function getSelectedCountForId(cartVouchers, voucherId) {
  const matching = cartVouchers.filter(
    (v) => String(v.id) === String(voucherId)
  );
  if (matching.length === 0) return 0;

  const maxCount = matching.reduce(
    (max, v) => Math.max(max, Number(v.count) || 0),
    0
  );
  return Math.max(matching.length, maxCount);
}

/** Sync category voucher counters with the selected voucher cart. */
function mergeVoucherListWithSelection(prevList, selectedVouchers) {
  const cartVouchers = Array.isArray(selectedVouchers) ? selectedVouchers : [];

  if (!Array.isArray(prevList) || prevList.length === 0) return prevList;

  if (cartVouchers.length === 0) {
    return prevList.map((item) => ({
      ...item,
      vouchers: Array.isArray(item.vouchers)
        ? item.vouchers.map((voucher) => ({
            ...voucher,
            count: 0,
            isCounterVisible: false,
          }))
        : [],
    }));
  }

  return prevList.map((item) => ({
    ...item,
    vouchers: Array.isArray(item.vouchers)
      ? item.vouchers.map((voucher) => {
          const slotCount = getSelectedCountForId(cartVouchers, voucher.id);
          return {
            ...voucher,
            count: slotCount,
            isCounterVisible: slotCount > 0,
          };
        })
      : [],
  }));
}

export const SelectVouchers = () => {
  const {
    voucherList,
    setVoucherList,
    selectedVouchers,
    voucherCount,
    voucherTotal,
    isVoucherCartActive,
    clearVoucherCart,
  } = useVoucherContext();

  const [loading, setLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState(null);
  const [stickyBlockHeight, setStickyBlockHeight] = useState(0);
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [selectedTerms, setSelectedTerms] = useState("");
  const navigate = useNavigate();
  const location = useLocation();
  const { isSticky, sentinelRef } = useSticky();

  const handleBack = () => {
    clearVoucherCart();
    clearSessionStorage();
    navigate('/');
  };

  const sectionRefs = useRef(new Map());
  const categoryRefs = useRef(new Map());
  const suppressObserverRef = useRef(false);
  const suppressTimerRef = useRef(null);
  const stickyHeaderRef = useRef(null);
  const hasScrolledToCategoryRef = useRef(false);
  const scrollToCategoryId = location.state?.scrollToCategoryId;

  const categoryIdsKey = useMemo(
    () => (voucherList?.length ? voucherList.map((c) => c.id).join(",") : ""),
    [voucherList]
  );

  const voucherListRef = useRef(voucherList);
  voucherListRef.current = voucherList;
  const selectedVouchersRef = useRef(selectedVouchers);
  selectedVouchersRef.current = selectedVouchers;

  const setSectionRef = useCallback((categoryId) => (el) => {
    const map = sectionRefs.current;
    if (el) map.set(categoryId, el);
    else map.delete(categoryId);
  }, []);

  const setCategoryRef = useCallback((categoryId) => (el) => {
    const map = categoryRefs.current;
    if (el) map.set(categoryId, el);
    else map.delete(categoryId);
  }, []);

  useLayoutEffect(() => {
    const el = stickyHeaderRef.current;
    if (!el) return;

    const measure = () => {
      setStickyBlockHeight(el.offsetHeight || 0);
    };

    measure();

    if (typeof ResizeObserver !== "undefined") {
      const ro = new ResizeObserver(() => measure());
      ro.observe(el);
      return () => ro.disconnect();
    }

    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, [isSticky, loading, categoryIdsKey]);

  const queryParams = useMemo(() => ({
    pagination: 0,
    is_image: 1,
    is_online: 1,
  }), []);

  const { data: queryData, isLoading, isError } = useGetVouchersList(queryParams);

  useEffect(() => {
    setLoading(isLoading);
  }, [isLoading]);

  useEffect(() => {
    if (isLoading || isError || !queryData) return;

    const voucherCategoryData = queryData?.data?.data?.list?.category || [];
    const apiData = normalizeVoucherCategories(voucherCategoryData).map(
      (category) => ({
        ...category,
        vouchers: Array.isArray(category.vouchers)
          ? category.vouchers.map((voucher) => ({
              ...voucher,
              count: 0,
              isCounterVisible: false,
            }))
          : [],
      })
    );

    // Apply current cart immediately so counters are selected after edit navigation.
    setVoucherList(
      mergeVoucherListWithSelection(apiData, selectedVouchersRef.current)
    );
  }, [queryData, isLoading, isError, setVoucherList]);

  const selectedVouchersKey = useMemo(
    () => JSON.stringify(selectedVouchers ?? []),
    [selectedVouchers]
  );

  // Keep catalog counters in sync when cart changes (and after load).
  useLayoutEffect(() => {
    if (loading) return;
    setVoucherList((prevList) => {
      if (!prevList?.length) return prevList;
      return mergeVoucherListWithSelection(prevList, selectedVouchersRef.current);
    });
  }, [loading, selectedVouchersKey, setVoucherList]);

  useEffect(() => {
    if (loading) {
      document.body.classList.add("overflow-hidden");
    } else {
      document.body.classList.remove("overflow-hidden");
    }

    return () => {
      document.body.classList.remove("overflow-hidden");
    };
  }, [loading]);

  const beginSuppressObserver = useCallback(() => {
    suppressObserverRef.current = true;
    if (suppressTimerRef.current) {
      clearTimeout(suppressTimerRef.current);
    }
    suppressTimerRef.current = setTimeout(() => {
      suppressObserverRef.current = false;
      suppressTimerRef.current = null;
    }, SCROLL_SUPPRESS_MS);
  }, []);

  useEffect(
    () => () => {
      if (suppressTimerRef.current) clearTimeout(suppressTimerRef.current);
    },
    []
  );

  const computeActiveSection = useCallback(() => {
    const list = voucherListRef.current;
    if (suppressObserverRef.current || !list?.length) return;

    const activationY =
      stickyHeaderRef.current?.getBoundingClientRect?.().bottom ?? stickyBlockHeight;

    let bestId = list[0].id;
    let bestTop = Number.NEGATIVE_INFINITY;

    for (const cat of list) {
      const el = sectionRefs.current.get(cat.id);
      if (!el) continue;
      const top = el.getBoundingClientRect().top;
      if (top <= activationY + 12 && top > bestTop) {
        bestTop = top;
        bestId = cat.id;
      }
    }

    setActiveCategory((prev) => (prev !== bestId ? bestId : prev));
  }, [stickyBlockHeight]);

  useEffect(() => {
    if (loading || !categoryIdsKey) return;

    let rafId = null;
    const scheduleCompute = () => {
      if (rafId != null) cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(() => {
        rafId = null;
        computeActiveSection();
      });
    };

    const observer =
      typeof IntersectionObserver !== "undefined"
        ? new IntersectionObserver(scheduleCompute, {
            root: null,
            rootMargin: "0px",
            threshold: [0, 0.05, 0.1, 0.25, 0.5, 0.75, 1],
          })
        : null;

    voucherListRef.current.forEach((cat) => {
      const el = sectionRefs.current.get(cat.id);
      if (el && observer) observer.observe(el);
    });

    window.addEventListener("scroll", scheduleCompute, { passive: true });
    window.addEventListener("resize", scheduleCompute);
    scheduleCompute();

    return () => {
      if (rafId != null) cancelAnimationFrame(rafId);
      window.removeEventListener("scroll", scheduleCompute);
      window.removeEventListener("resize", scheduleCompute);
      observer?.disconnect();
    };
  }, [loading, categoryIdsKey, computeActiveSection]);

  useEffect(() => {
    if (activeCategory == null) return;
    const btn = categoryRefs.current.get(activeCategory);
    if (!btn) return;
    const container = btn.parentElement;
    if (!container) return;
    const cRect = container.getBoundingClientRect();
    const bRect = btn.getBoundingClientRect();
    const delta = bRect.left + bRect.width / 2 - (cRect.left + cRect.width / 2);
    container.scrollBy({ left: delta, behavior: "smooth" });
  }, [activeCategory]);

  const handleCategoryClick = useCallback(
    (categoryId) => {
      const el = sectionRefs.current.get(categoryId);
      if (!el) return;

      setActiveCategory(categoryId);
      beginSuppressObserver();
      el.scrollIntoView({ behavior: "smooth", block: "start" });
    },
    [beginSuppressObserver]
  );

  const findSectionEl = useCallback((categoryId) => {
    if (categoryId == null) return null;
    const direct = sectionRefs.current.get(categoryId);
    if (direct) return { el: direct, id: categoryId };

    for (const [id, node] of sectionRefs.current.entries()) {
      if (String(id) === String(categoryId)) {
        return { el: node, id };
      }
    }
    return null;
  }, []);

  // After edit from Billing Details: restore selection UI, then scroll to that category.
  useEffect(() => {
    if (loading || scrollToCategoryId == null || hasScrolledToCategoryRef.current) {
      return;
    }

    let timeoutId = null;
    const rafId = requestAnimationFrame(() => {
      timeoutId = setTimeout(() => {
        const match = findSectionEl(scrollToCategoryId);
        if (!match?.el) return;

        hasScrolledToCategoryRef.current = true;
        setActiveCategory(match.id);
        beginSuppressObserver();
        match.el.scrollIntoView({ behavior: "smooth", block: "start" });
        navigate(".", { replace: true, state: {} });
      }, 80);
    });

    return () => {
      cancelAnimationFrame(rafId);
      if (timeoutId != null) clearTimeout(timeoutId);
    };
  }, [
    loading,
    scrollToCategoryId,
    categoryIdsKey,
    selectedVouchersKey,
    stickyBlockHeight,
    findSectionEl,
    beginSuppressObserver,
    navigate,
  ]);

  const noVoucherSelected = useMemo(() => {
    return Number(voucherCount ?? 0) === 0;
  }, [voucherCount]);

  const showCartPadding = isVoucherCartActive;
  const cartDisplayTotal = voucherTotal ?? 0;
  const cartDisplayCount = voucherCount ?? 0;
  const voucherListLength = voucherList?.length ?? 0;

  const handleReadTerms = (termsText) => {
    setSelectedTerms(termsText || "");
    setShowTermsModal(true);
  };

  return (
    <>
      <VoucherTermsModal
        active={showTermsModal}
        onClose={() => setShowTermsModal(false)}
        termsText={selectedTerms}
      />
      <div className="sticky-sentinel" ref={sentinelRef} aria-hidden="true" />
      <div ref={stickyHeaderRef} className={`sticky-header ${isSticky ? "is-sticky" : ""} pb-0`}>
        <Header
          title={voucherListLength > 0 ? `Select Vouchers` : "Vouchers"}
          noVoucherSelected={noVoucherSelected}
          onBack={handleBack}
        />
        {voucherList.length > 0 && (
          <div className={`${styles.voucherCategoryList} container-md space-x hide-scrollbar`}>
            {voucherList.map((category) => (
              <button
                key={category.id}
                type="button"
                ref={setCategoryRef(category.id)}
                className={`${styles.voucherCategoryBtn}${
                  activeCategory === category.id ? ` ${styles.isActive}` : ""
                }`}
                onClick={() => handleCategoryClick(category.id)}
                title={category.name}
                aria-label={category.name}
              >
                {category.name}
              </button>
            ))}
          </div>
        )}
      </div>

      <div
        className={`space-x space-y container-md ${
          showCartPadding ? "cart-space-b" : ""
        }`}
      >
        {loading ? (
          <Loader />
        ) : (
          <div>
            {voucherList.length > 0 ? (
              voucherList.map((category) => (
                <div
                  key={category.id}
                  ref={setSectionRef(category.id)}
                  style={{
                    scrollMarginTop: stickyBlockHeight + SECTION_SCROLL_OFFSET,
                  }}
                >
                  <Voucher
                    id={category?.id}
                    title={category?.name}
                    description={category?.description}
                    image={category?.image}
                    vouchers={category?.vouchers}
                    terms={category?.terms}
                    termsAndCondition={category?.terms_and_condition}
                    onReadTerms={handleReadTerms}
                  />
                </div>
              ))
            ) : (
              <Placeholder
                heading="Vouchers not found"
                para="Please try again later"
                icon={<CloseCircleIcon />}
              />
            )}
          </div>
        )}
      </div>

      <VoucherCart
        btnLabel="Add"
        btnClick={() => navigate("/billing-details")}
        isCartActive={showCartPadding}
        disabled={false}
        displayTotal={cartDisplayTotal}
        displayCount={cartDisplayCount}
      />
    </>
  );
};
